import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { useAuth } from '../../lib/authContext';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { loadStoredBatches } from '../../lib/onboardingBatches';
import {
  confirmPayload,
  groupTitle,
  proposalsByRow,
  sourceLabel,
  statusLine,
  type EditableField,
  type Proposal,
  type ProposalGroup,
  type ReviewPayload,
  type ReviewRow,
} from '../../lib/importReview';
import { MappingStep } from '../../components/import/MappingStep';
import { RowTable } from '../../components/import/RowTable';
import { ChatPanel } from '../../components/import/ChatPanel';

/**
 * SIL-1617 - import review page (spec 2026-10-06-import-onboarding-agent-design
 * section 6.1), following the ERP triage page pattern
 * (financials/triage.$batchId.tsx). Rules only, no LLM.
 *
 *   mapping step (non-template files)  ->  status header  ->  fix cards
 *   ->  editable row table  ->  Submit for review
 *
 * Every write goes through the review routes in apps/api/src/routes/client/
 * import.ts, which funnel into services/import/apply-fix.ts. Non-admins see the
 * page read-only; the server is the real 403 boundary.
 */
export const Route = createFileRoute('/import/$batchId')({
  component: ImportReviewPage,
});

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    await throwIfNotOk(res);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

const TONE_STYLES = {
  green: 'border-status-green/30 bg-status-green/10 text-status-green',
  amber: 'border-status-amber/30 bg-status-amber/10 text-status-amber',
  red: 'border-status-red/30 bg-status-red/10 text-status-red',
} as const;

function ImportReviewPage() {
  const { batchId } = Route.useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();

  const [actionError, setActionError] = useState<string | null>(null);
  const [focusGroup, setFocusGroup] = useState<ProposalGroup | null>(null);

  const reviewQ = useQuery({
    queryKey: qk.importBatch.review(batchId),
    queryFn: async () => json<ReviewPayload>(await api.api.client.import.batches[':batchId'].review.$get({ param: { batchId } })),
  });
  const review = reviewQ.data;
  const stagedReady = !!review && !review.needsMapping;

  const proposalsQ = useQuery({
    queryKey: qk.importBatch.proposals(batchId),
    enabled: stagedReady && !review?.locked,
    queryFn: async () => {
      const proposals: Proposal[] = [];
      let groups: ProposalGroup[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < 20; page++) {
        const body = await json<{ proposals: Proposal[]; groups: ProposalGroup[]; nextCursor: string | null }>(
          await api.api.client.import.batches[':batchId'].proposals.$get({
            param: { batchId },
            query: { status: 'proposed', limit: '200', ...(cursor ? { cursor } : {}) },
          }),
        );
        proposals.push(...body.proposals);
        groups = body.groups;
        if (!body.nextCursor) break;
        cursor = body.nextCursor;
      }
      return { proposals, groups };
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: qk.importBatch.all(batchId) });

  /** Run a write; surface the server's message; refresh everything after (also on error: the row may have gone stale). */
  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => {
      setActionError(null);
      try {
        await fn();
      } finally {
        await refresh();
      }
    },
    onError: (e: unknown) => setActionError((e as Error).message),
  });
  const act = (fn: () => Promise<unknown>) => run.mutateAsync(fn).catch(() => undefined);
  const busy = run.isPending;

  const confirmMapping = (columns: Parameters<typeof confirmPayload>[0]) =>
    act(async () =>
      json(await api.api.client.import.batches[':batchId'].mapping.confirm.$post({ param: { batchId }, json: confirmPayload(columns) })),
    );
  const applyProposal = (proposalId: string) =>
    act(async () => json(await api.api.client.import.batches[':batchId'].proposals[':proposalId'].apply.$post({ param: { batchId, proposalId } })));
  const dismissProposal = (proposalId: string) =>
    act(async () => json(await api.api.client.import.batches[':batchId'].proposals[':proposalId'].dismiss.$post({ param: { batchId, proposalId } })));
  const applyGroup = (g: ProposalGroup) =>
    act(async () => {
      // The API caps one call; loop until the group is drained (or a call makes no progress).
      for (let i = 0; i < 20; i++) {
        const res = await json<{ applied: number; stale: number; failed: number; truncated: boolean }>(
          await api.api.client.import.batches[':batchId'].proposals['apply-group'].$post({
            param: { batchId },
            json: { groupKey: g.groupKey, ruleCode: g.ruleCode },
          }),
        );
        if (!res.truncated || res.applied + res.stale + res.failed === 0) break;
      }
      setFocusGroup(null);
    });
  const editCell = (row: ReviewRow, field: EditableField, value: string | null) =>
    act(async () =>
      json(await api.api.client.import.batches[':batchId'].rows[':rowId'].$patch({ param: { batchId, rowId: row.id }, json: { field, value } })),
    );
  const submit = () => act(async () => json(await api.api.client.import.batches[':batchId'].submit.$post({ param: { batchId } })));

  const proposals = proposalsQ.data?.proposals ?? [];
  const groups = proposalsQ.data?.groups ?? [];
  const byRow = useMemo(() => proposalsByRow(proposals), [proposals]);

  if (reviewQ.isLoading) return <LoadingShell rows={4} />;
  if (reviewQ.error || !review) {
    return (
      <div className="space-y-4">
        <PageHeader title="Review import" />
        <p role="alert" className="text-sm text-status-red">
          {(reviewQ.error as Error | null)?.message ?? 'Import not found.'}{' '}
          <Link to="/onboarding" className="font-medium underline">
            Back to import
          </Link>
        </p>
      </div>
    );
  }

  // Uploaded from the onboarding wizard: send the user back to it, state intact.
  const fromWizard = loadStoredBatches().some((b) => b.batchId === batchId);
  const submitted = review.readyForReviewAt !== null;
  const status = statusLine(review);
  const canEdit = isAdmin && !review.locked;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Review import"
        subtitle={review.filename ?? 'Uploaded file'}
        actions={
          fromWizard ? (
            <Link to="/onboarding" className="text-sm font-medium text-brand-600 hover:underline">
              Continue setup
            </Link>
          ) : (
            <Link to="/assets" className="text-sm font-medium text-brand-600 hover:underline">
              Back to assets
            </Link>
          )
        }
      />
      {!isAdmin && (
        <p className="text-xs text-status-amber">Read-only - only an org admin can change or submit this import.</p>
      )}
      {actionError && (
        <p role="alert" className="text-sm text-status-red">
          {actionError}
        </p>
      )}

      {review.needsMapping && review.mapping ? (
        <MappingStep mapping={review.mapping} canEdit={isAdmin} busy={busy} onConfirm={confirmMapping} />
      ) : (
        <>
          <Card padding="md" aria-label="Import status">
            <div
              role="status"
              data-testid="import-status"
              data-tone={status.tone}
              className={cn('flex items-center gap-2 rounded-md border px-4 py-3 text-sm font-medium', TONE_STYLES[status.tone])}
            >
              {status.tone === 'red' ? <XCircle className="size-4" /> : status.tone === 'amber' ? <AlertTriangle className="size-4" /> : <CheckCircle2 className="size-4" />}
              {status.text}
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <Stat label="Accepted" value={review.counts.accepted} testId="count-accepted" />
              <Stat label="Rejected" value={review.counts.rejected} testId="count-rejected" />
              <Stat label="Needs review" value={review.counts.needsReview} testId="count-needs-review" />
              <Stat label="Excluded" value={review.counts.excluded} testId="count-excluded" />
            </dl>
          </Card>

          <ChatPanel
            batchId={batchId}
            isAdmin={isAdmin}
            locked={review.locked}
            escalatedAt={review.escalatedAt}
            busy={busy}
            onApply={applyProposal}
            onDismiss={dismissProposal}
          />

          {review.locked && !submitted && (
            <p className="text-sm text-muted-foreground">
              Nothing was imported from this file, so it can no longer be changed here. Fix the file and{' '}
              <Link to="/onboarding" className="font-medium text-brand-600 hover:underline">
                upload it again
              </Link>
              .
            </p>
          )}

          {submitted && (
            <p className="rounded-md border border-status-green/30 bg-status-green/10 px-4 py-3 text-sm text-status-green">
              Submitted for review. The SiliconX team will review your inventory; assets appear in your portal once approved. This import can no longer be changed.
            </p>
          )}

          {!review.locked && groups.length > 0 && (
            <section aria-label="Suggested fixes" className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground">Suggested fixes</h2>
              {groups.map((g) => (
                <FixCard
                  key={`${g.groupKey}|${g.source}|${g.ruleCode}`}
                  group={g}
                  canEdit={isAdmin}
                  busy={busy}
                  focused={focusGroup?.groupKey === g.groupKey && focusGroup.ruleCode === g.ruleCode}
                  onApplyAll={() => applyGroup(g)}
                  onReview={() => setFocusGroup(g)}
                  onDismiss={async () => {
                    // No dismiss-group route: dismiss each open proposal in the card.
                    const ids = proposals.filter((p) => p.groupKey === g.groupKey && p.ruleCode === g.ruleCode && p.source === g.source).map((p) => p.id);
                    await act(async () => {
                      let done = 0;
                      try {
                        for (const id of ids) {
                          await json(await api.api.client.import.batches[':batchId'].proposals[':proposalId'].dismiss.$post({ param: { batchId, proposalId: id } }));
                          done++;
                        }
                      } catch (e) {
                        throw new Error(`Dismissed ${done} of ${ids.length} suggestions, then failed: ${(e as Error).message}`);
                      }
                    });
                  }}
                />
              ))}
            </section>
          )}

          <RowTable
            batchId={batchId}
            canEdit={canEdit}
            busy={busy}
            proposalsByRow={byRow}
            focusRows={
              focusGroup
                ? new Set(
                    proposals
                      .filter((p) => p.groupKey === focusGroup.groupKey && p.ruleCode === focusGroup.ruleCode && p.rowNum !== null)
                      .map((p) => p.rowNum as number),
                  )
                : null
            }
            onClearFocus={() => setFocusGroup(null)}
            onEdit={editCell}
            onApply={applyProposal}
            onDismiss={dismissProposal}
          />

          {!review.locked && (
            <div className="flex items-center justify-end gap-3">
              {review.counts.accepted === 0 && <p className="text-xs text-muted-foreground">Fix at least one row before submitting.</p>}
              {isAdmin && (
                <Button onClick={submit} disabled={busy || review.counts.accepted === 0}>
                  {busy ? 'Working...' : 'Submit for review'}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd data-testid={testId} className="text-lg font-semibold tabular-nums text-foreground">
        {value}
      </dd>
    </div>
  );
}

function FixCard({
  group,
  canEdit,
  busy,
  focused,
  onApplyAll,
  onReview,
  onDismiss,
}: {
  group: ProposalGroup;
  canEdit: boolean;
  busy: boolean;
  focused: boolean;
  onApplyAll: () => void;
  onReview: () => void;
  onDismiss: () => void;
}) {
  return (
    <Card padding="md" data-testid="fix-card" data-rule={group.ruleCode} className={cn(focused && 'ring-2 ring-brand-500')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            {groupTitle(group)}
            <StatusPill tone="neutral" title={group.source === 'rule' ? 'Found by a built-in check' : 'Suggested by AI review'}>
              {sourceLabel(group.source)}
            </StatusPill>
          </p>
          {group.reason && <p className="mt-0.5 text-xs text-muted-foreground">{group.reason}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={onReview} disabled={busy}>
            Review rows
          </Button>
          {canEdit && (
            <>
              <Button size="sm" onClick={onApplyAll} disabled={busy}>
                Apply all
              </Button>
              <Button size="sm" variant="secondary" onClick={onDismiss} disabled={busy}>
                Dismiss
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
